/**
 * Reglas compartidas: qué métodos de pago usan ID de transacción a nivel pedido
 * o por fila en pagos parciales (alineado con useOrderValidation + OrderPaymentInfo).
 */

export function normalizeOrderPaymentMethod(value: unknown): string {
  if (value === undefined || value === null) return ''
  return String(value).trim().toLowerCase()
}

/** Pedido con un solo medio: métodos que usan y validan `extraDetails.transaction_id`. */
export function singleOrderPaymentMethodUsesTransactionId(pm: string): boolean {
  const m = normalizeOrderPaymentMethod(pm)
  return m === 'wompi' || m === 'mpago' || m === 'bacs' || m === 'addi'
}

export function orderPaymentMethodIsPartOrPlanSepare(pm: string): boolean {
  const m = normalizeOrderPaymentMethod(pm)
  return m === 'part' || m === 'plan_separe'
}

/**
 * Fila de pago parcial que exige `id_transaccion` (misma lógica que useOrderValidation).
 */
export function parcialRowRequiresTransactionId(
  metodoPagoRow: unknown,
  orderPaymentMethodPrincipal: unknown
): boolean {
  const r = normalizeOrderPaymentMethod(metodoPagoRow)
  const principal = normalizeOrderPaymentMethod(orderPaymentMethodPrincipal)
  if (r === 'wompi' || r === 'mpago' || r === 'bacs') return true
  if (r === 'addi' && principal === 'plan_separe') return true
  return false
}

/** Métodos de fila en part/plan separe que no tienen ID de pasarela (no consultar lookup). */
const PARCIAL_ROW_METODOS_SIN_ID_TRANSACCION_LOOKUP = new Set(['cash', 'cod', 'cmdm'])

/**
 * En pagos parciales / plan separe puede haber **varios** `id_transaccion` distintos (una por fila).
 * Para el lookup previo al guardar, se considera cualquier fila con ID no vacío salvo métodos
 * que no usan transacción de pasarela (efectivo, contra entrega, crédito MDM).
 */
export function parcialRowContributesTransactionIdForLookup(metodoPagoRow: unknown): boolean {
  const m = normalizeOrderPaymentMethod(metodoPagoRow)
  if (!m) return true
  return !PARCIAL_ROW_METODOS_SIN_ID_TRANSACCION_LOOKUP.has(m)
}

/**
 * Al cambiar el método de pago del pedido: si el nuevo método no conserva el ID
 * único de transacción (o es Bold / parciales, donde el ID se maneja aparte),
 * hay que vaciar `extraDetails.transaction_id` para no enviarlo por error.
 */
function shouldClearSingleOrderTransactionIdOnPaymentMethodChange(
  newPaymentMethod: unknown
): boolean {
  const pm = normalizeOrderPaymentMethod(newPaymentMethod)
  if (orderPaymentMethodIsPartOrPlanSepare(pm)) return true
  if (pm === 'bold') return true
  if (singleOrderPaymentMethodUsesTransactionId(pm)) return false
  return true
}

/** Considera también el método anterior (p. ej. Bold → Wompi debe borrar el link viejo). */
export function shouldClearSingleOrderTransactionIdWhenPaymentMethodChanges(
  previousPaymentMethod: unknown,
  newPaymentMethod: unknown
): boolean {
  if (shouldClearSingleOrderTransactionIdOnPaymentMethodChange(newPaymentMethod)) return true
  const n = normalizeOrderPaymentMethod(newPaymentMethod)
  const p = normalizeOrderPaymentMethod(previousPaymentMethod)
  if (singleOrderPaymentMethodUsesTransactionId(n) && p === 'bold') return true
  return false
}
